import { type ReactNode } from 'react'
import Image from 'next/image'
import { AppInfo } from '@/components/content/app/AppInfo'
import { isDiscontinued } from '@/lib/app-categories/discontinued'
import VideoAppChip from '@/components/content/video-segment/VideoAppChip'
import Award from '@/components/ui/Award'
import CriteriaIcons from '@/components/ui/CriteriaIcons'
import Link from '@/components/ui/Link'
import RichText from '@/components/content/common/RichText'
import Section from '@/components/content/common/Section'
import { Icon } from '@/icons/Icon'
import { cn, toSlug, isPopulatedObject } from '@/lib/utils'
import { generateStructure, renderContent } from '@/lib/content'
import { iconMap } from '@/icons/map'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { processAffiliateLink } from '@/lib/formatting'
import type { Block } from '@/types/app'
import type { ResolvedVideoApp } from '@/lib/lexical-json/types'
import type {
  Deal,
  Media,
  AppCategoryRecommendation,
  Review,
  ReviewCategoryCriterionRating,
  App,
  Category,
} from '@/payload-types'

type CompareWithApp = Pick<App, 'id' | 'slug'>

type AppOverviewProps = {
  app: App
  rank?: number
  category: Category
  theme?: 'light' | 'dark'
  children?: ReactNode
  description?: string
  bestFor?: string
  verdict?: boolean
  compareWithApp?: CompareWithApp
  appCategoryRecommendation?: AppCategoryRecommendation
  serverRatings?: Pick<
    ReviewCategoryCriterionRating,
    'rating' | 'isApplicable' | 'categoryCriterion'
  >[]
  serverDeals?: Deal[]
  videoAppChip?: ResolvedVideoApp
  // Direction the app card's overflow menu opens. Defaults to 'down'; callers inside an
  // overflow-clipping container (e.g. BestVerdict's Slider) pass 'up' so the menu isn't clipped.
  position?: 'down' | 'up'
}

// Checks if Lexical rich text has actual text content (not just empty structure)
function hasRichTextContent(children: Block[] | undefined): boolean {
  if (!children?.length) return false
  return children.some(
    (block) =>
      (typeof block.text === 'string' && block.text.trim().length > 0) ||
      ('children' in block &&
        Array.isArray(block.children) &&
        hasRichTextContent(block.children as Block[])),
  )
}

export default function AppOverview({
  app,
  rank,
  category,
  theme,
  children,
  description,
  bestFor: bestForOverride,
  verdict,
  compareWithApp,
  appCategoryRecommendation,
  serverRatings,
  serverDeals,
  videoAppChip,
  position = 'down',
}: AppOverviewProps) {
  const [review] = (app?.review?.docs || []) as Review[]

  const appCategoryRecommendations = app.categoryRecommendations
    ?.docs as AppCategoryRecommendation[]

  const appCategory =
    appCategoryRecommendation ??
    appCategoryRecommendations?.find((acr) => {
      return !isPopulatedObject<Category>(acr.category)
        ? acr.category === category?.id
        : (acr.category as Category).id === category?.id
    })

  const recommendation = (appCategory?.recommendation ??
    Recommendation.NotRecommended) as Recommendation
  const discontinued = appCategory ? isDiscontinued(appCategory) : false

  const affiliate = processAffiliateLink(app.affiliateLink, app.slug, app.name, app.partnershipTier)
  const link = affiliate.url
  const acrTldr = appCategory?.tldr?.root?.children as Block[] | undefined
  const tldr = (hasRichTextContent(acrTldr) ? acrTldr : review?.tldr?.root?.children) as Block[]
  const bestFor =
    bestForOverride || appCategory?.bestFor || review?.bestFor || app.description || ''
  const homepageImage = app.homepageImage as Media
  const homepageImageUrl = homepageImage?.url
  const acrSummary = appCategory?.summary?.root?.children as Block[] | undefined
  const reviewSummary = review?.summary?.root?.children as Block[] | undefined
  const summary = hasRichTextContent(acrSummary) ? acrSummary : reviewSummary
  const resolvedHref: string | undefined = app.slug ? `/apps/${app.slug}` : undefined
  const summarySlugPrefix = toSlug(app.name)

  const { roots, preamble: preambleBlocks } = summary
    ? generateStructure({ content: summary })
    : { roots: [], preamble: [] }
  const { sections, preamble: summaryPreamble } = renderContent({
    roots,
    preamble: preambleBlocks,
    rootTag: 3,
    slugPrefix: summarySlugPrefix,
  })

  if (verdict) {
    return (
      <div className="relative flex w-full min-w-48 max-w-80 flex-col items-center gap-6 rounded-2xl bg-gradient-to-b from-primary-500/15 to-transparent px-10 py-8 before:absolute before:inset-0 before:rounded-2xl before:border before:border-white/10">
        {!discontinued && <Award recommendation={recommendation} rank={rank} size="h-8 w-auto" />}

        <AppInfo
          tag="h3"
          app={app}
          recommendation={discontinued ? undefined : recommendation}
          bestFor={bestFor}
          theme={theme}
          description={description}
          category={category}
          verdict={verdict}
          compareWithApp={compareWithApp}
          serverRatings={serverRatings}
          serverDeals={serverDeals}
          position={position}
        />
      </div>
    )
  }

  const isOdd = rank ? rank % 2 === 0 : false

  return (
    <div
      data-section-app
      data-section-id={app.slug}
      data-name="Overview"
      className={cn(
        'flex flex-col gap-10 py-5 xl:gap-20',
        isOdd ? 'md:flex-row' : 'md:flex-row-reverse',
      )}
    >
      <div className="relative z-20 w-full md:w-2/5">
        {children}
        <div
          className={cn(
            'flex w-full flex-col items-center gap-6 md:items-start',
            summary ? 'md:sticky md:top-16 md:pt-16' : '',
          )}
        >
          <div className="relative z-30 flex w-full flex-col items-center gap-8 md:flex-row">
            {!discontinued && (
              <Award recommendation={recommendation} rank={rank} size="h-8 w-auto" />
            )}

            <CriteriaIcons
              app={app}
              categoryId={category?.id}
              reviewCriterionRatings={review?.reviewCategoryCriterionRating?.docs?.filter(
                (r): r is Exclude<typeof r, number> => typeof r !== 'number',
              )}
            />
          </div>
          <AppInfo
            app={app}
            recommendation={discontinued ? undefined : recommendation}
            bestFor={bestFor}
            responsive={true}
            theme={theme}
            description={description}
            tldr={tldr}
            category={category}
            summary={true}
            swap={!isOdd}
            compareWithApp={compareWithApp}
            serverRatings={serverRatings}
            serverDeals={serverDeals}
            navigateHref={resolvedHref}
            position={position}
          />
        </div>
      </div>

      <div className="relative z-10 flex w-full flex-col gap-4 md:w-3/5 md:gap-8 lg:gap-16">
        <Link
          href={link}
          passReferrerUrl={affiliate.passReferrerUrl}
          sponsored={affiliate.isSponsored}
          className="group relative aspect-video w-full cursor-pointer rounded-2xl border border-white/20 bg-neutral-950 p-3 after:absolute after:-bottom-px after:-left-px after:h-1/2 after:w-[calc(100%+2px)] after:bg-gradient-to-t after:from-black after:to-transparent after:content-['']"
        >
          {homepageImageUrl && (
            <Image
              src={homepageImageUrl}
              alt={app.name}
              width={homepageImage.width ?? 1280}
              height={homepageImage.height ?? 720}
              sizes="(min-width: 768px) 640px, 100vw"
              className="h-full w-full rounded-lg object-cover"
            />
          )}
          <div className="absolute -bottom-3 z-30 flex w-full items-center justify-center gap-3 text-center font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
            <span>Go to {app.name} site</span>
            <Icon name="share-right" />
          </div>
        </Link>

        {summary && (sections.length > 0 || summaryPreamble) ? (
          <div className="flex flex-col">
            {summaryPreamble && <div className="px-5 pb-4 text-zinc-300">{summaryPreamble}</div>}
            {sections.map((section, index) => {
              const sectionId =
                section.slug ||
                `${summarySlugPrefix}-${toSlug(section.title ?? `section-${index}`)}`

              return (
                <Section
                  key={sectionId}
                  sectionId={sectionId}
                  title={section.title || ''}
                  icon={iconMap(section.title || '')}
                  copy={false}
                  join={true}
                  depth={section.depth}
                  rootTag={section.rootTag}
                  offset={80}
                >
                  {index === 0 && videoAppChip && <VideoAppChip videoApp={videoAppChip} />}
                  {section.content as ReactNode}
                </Section>
              )
            })}
          </div>
        ) : (
          <Section
            key={`${app.slug}-summary`}
            sectionId={`${app.slug}-summary`}
            title={`${app.name} Summary`}
            icon={iconMap(`${app.name} Summary`)}
            isChild={true}
            copy={false}
            tooltip={false}
            offset={80}
          >
            {videoAppChip && <VideoAppChip videoApp={videoAppChip} />}
            <RichText content={summary ?? []} />
          </Section>
        )}
      </div>
    </div>
  )
}
